import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Buildings, Lock } from "@phosphor-icons/react";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Left visual panel */}
      <div className="hidden lg:flex relative bg-[#002FA7] overflow-hidden">
        <img
          src="https://images.pexels.com/photos/11991914/pexels-photo-11991914.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 bg-white text-[#002FA7] grid place-items-center rounded-sm">
              <Buildings size={22} weight="bold" />
            </div>
            <span className="font-heading text-xl font-bold tracking-tight">GND Leads</span>
          </div>
          <div>
            <h1 className="font-heading text-5xl font-bold tracking-tight leading-tight">
              Neugründungen<br />finden. Anschreiben.<br />Gewinnen.
            </h1>
            <p className="mt-6 text-white/80 max-w-md text-sm leading-relaxed">
              Recherchiere Neugründungen aus öffentlichen Quellen, erkenne Duplikate
              automatisch und erstelle personalisierte Serienbriefe als PDF.
            </p>
          </div>
          <div className="text-xs uppercase tracking-widest text-white/50">
            Gastro Next Digital
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8 bg-[#FAFAFA]">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="mb-8">
            <div className="flex lg:hidden items-center gap-2 mb-6">
              <div className="h-8 w-8 bg-[#002FA7] text-white grid place-items-center rounded-sm">
                <Buildings size={18} weight="bold" />
              </div>
              <span className="font-heading text-lg font-bold">GND Leads</span>
            </div>
            <h2 className="font-heading text-3xl font-bold tracking-tight">Anmelden</h2>
            <p className="text-sm text-zinc-500 mt-1">Melde dich an, um fortzufahren.</p>
          </div>

          {error && (
            <div
              className="mb-4 text-sm text-[#E53935] bg-red-50 border border-red-200 px-3 py-2 rounded-sm"
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-zinc-500">
                E-Mail
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="mt-1.5 focus:ring-2 focus:ring-[#002FA7]/40 focus:border-[#002FA7]"
                placeholder="name@firma.de"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-zinc-500">
                Passwort
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="mt-1.5 focus:ring-2 focus:ring-[#002FA7]/40 focus:border-[#002FA7]"
                placeholder="••••••••"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit-button"
            className="w-full mt-6 bg-[#002FA7] hover:bg-[#001D6E] text-white rounded-sm h-11"
          >
            <Lock size={16} weight="bold" className="mr-2" />
            {loading ? "Anmelden…" : "Anmelden"}
          </Button>
        </form>
      </div>
    </div>
  );
}
